import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/session';
import { useToast } from './Toast';
import './FeedbackPopup.css';

type Status = 'idle' | 'sending' | 'success' | 'error';

const CATEGORIES = [
  { value: 'tech', label: 'Tech' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'idea', label: 'Idea' },
  { value: 'billing', label: 'Billing' },
  { value: 'other', label: 'Other' },
] as const;

type CategoryValue = (typeof CATEGORIES)[number]['value'];

export function FeedbackPopup() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<CategoryValue | ''>('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const { profile } = useSession();
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim() || !category || !profile) return;
    setStatus('sending');

    try {
      const { error } = await supabase.from('feedback').insert({
        user_id: profile.id,
        page_path: window.location.pathname,
        message: body.trim().slice(0, 4000),
        metadata: { category },
      });
      if (error) throw error;
      setStatus('success');
      setBody('');
      setCategory('');
      setTimeout(() => {
        setOpen(false);
        setStatus('idle');
        toast.success('Thanks, message received.');
      }, 1400);
    } catch (err) {
      console.error('[contact] submit failed', err);
      setStatus('error');
    }
  }

  return (
    <>
      <button
        type="button"
        className="feedback-fab"
        onClick={() => setOpen(true)}
        aria-label="Contact us"
      >
        Contact
      </button>

      {open && (
        <div className="feedback-modal" role="dialog" aria-modal="true" aria-labelledby="feedback-title">
          <div className="feedback-modal__scrim" onClick={() => setOpen(false)} />
          <form className="feedback-modal__panel" onSubmit={submit}>
            <header className="feedback-modal__head">
              <p className="kicker">Contact</p>
              <h2 id="feedback-title">Get in touch!</h2>
              <button
                type="button"
                className="feedback-modal__close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </header>

            <label className="feedback-modal__label" htmlFor="contact-category">
              Category
            </label>
            <div className="feedback-modal__select-wrap">
              <select
                id="contact-category"
                required
                value={category}
                onChange={(e) => setCategory(e.target.value as CategoryValue)}
                className="feedback-modal__select"
              >
                <option value="" disabled>
                  Pick one…
                </option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <label className="feedback-modal__label" htmlFor="contact-message">
              Message
            </label>
            <textarea
              id="contact-message"
              required
              placeholder="Tell us what's going on."
              maxLength={4000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />

            <p className="feedback-modal__note">
              This goes straight to the team, and we love hearing from you!
            </p>

            <div className="feedback-modal__actions">
              <button
                type="submit"
                className="btn btn--primary btn--md"
                disabled={status === 'sending' || !body.trim() || !category}
              >
                {status === 'sending' ? 'Sending…' : 'Send'}
              </button>
            </div>

            {status === 'success' && <p className="feedback-modal__status feedback-modal__status--success">Thanks, message received.</p>}
            {status === 'error' && <p className="feedback-modal__status feedback-modal__status--error">Something broke. Try again.</p>}
          </form>
        </div>
      )}
    </>
  );
}
